import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { LocationPinIcon } from '@/assets/icons';
import CategoryItem from '@/components/CategroryItem';
import { useHomeCategorySection } from '@/features/CategoryKind/hooks/useHomeCategorySection';
import { useViewportStore } from '@/store/useViewportStore';
import Link from 'next/link';
import CategoryListSkeleton from './skeletons/CategoryListSkeleton';

const CategoryList = () => {
  const { categories, isPending, type } = useHomeCategorySection();
  const isMobile = useViewportStore((store) => store.isMobile);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const mobileCategories = categories?.slice(0, 8);

  useEffect(() => {
    if (isMobile) return;

    const wrapper = wrapperRef.current;
    if (wrapper) {
      const checkScroll = () => {
        const isNowScrollable = wrapper.scrollWidth > wrapper.clientWidth;
        setIsScrollable(isNowScrollable);
        if (isNowScrollable) {
          wrapper.scrollLeft = 0; // 💡 첫 아이템 완전히 보이게 보정
        }
      };

      checkScroll();
      window.addEventListener('resize', checkScroll);
      return () => window.removeEventListener('resize', checkScroll);
    }
  }, [categories, isMobile]);

  if (isMobile) {
    return (
      <MobileCategoryCard>
        <MobileCategoryGrid>
          {isPending ? (
            <CategoryListSkeleton count={10} mobile />
          ) : (
            mobileCategories?.map((category) => (
              <CategoryItem type={type} key={category.id} {...category} />
            ))
          )}
        </MobileCategoryGrid>
        <AroundMeLink href="/around-me">
          <AroundMeIcon width={11} height={11} aria-hidden="true" />내 주변 지도
        </AroundMeLink>
      </MobileCategoryCard>
    );
  }

  return (
    <CategoryWrapper ref={wrapperRef} $scrollable={isScrollable}>
      {isPending ? (
        <CategoryListSkeleton />
      ) : (
        categories?.map((category) => <CategoryItem type={type} key={category.id} {...category} />)
      )}
    </CategoryWrapper>
  );
};

export default CategoryList;

const CategoryWrapper = styled.div<{ $scrollable: boolean }>`
  display: flex;
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  gap: 22px;
  margin: 0 0 28px;
  padding: 0 8px 4px;
  justify-content: ${({ $scrollable }) => ($scrollable ? 'flex-start' : 'center')};
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  align-items: flex-start;

  &::-webkit-scrollbar {
    display: none;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray300};
    border-radius: 4px;
  }

  > a {
    width: 88px;
    gap: 10px;
    flex-shrink: 0;
  }

  > a > div:first-child {
    width: 72px;
    height: 72px;
    padding: 10px;
    background: transparent;
  }

  > a > div:last-child p {
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.gray1000};
    white-space: nowrap;
  }
`;

const MobileCategoryCard = styled.div`
  margin: 8px 0 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.white};
  padding: 12px 12px 10px;
`;

const MobileCategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 8px;
`;

const AroundMeLink = styled(Link)`
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;

  &::after {
    content: '>';
    font-size: 12px;
  }
`;

const AroundMeIcon = styled(LocationPinIcon)`
  flex-shrink: 0;
`;
